import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, ClipboardList, Settings } from 'lucide-react';
import { useState } from 'react';

import { EhrAccessCard } from '@apps/web/components/admin/ehr_access_card';
import { ExternalPhoneForwardingCard } from '@apps/web/components/admin/external_phone_forwarding_card';
import { OnboardingReviewCard } from '@apps/web/components/admin/onboarding_review_card';
import { OrganizationChecklistPanel } from '@apps/web/components/admin/organization_checklist_panel';
import { OrganizationFormModal } from '@apps/web/components/admin/organization_form_modal';
import { OrganizationSyncSettings } from '@apps/web/components/admin/organization_sync_settings';
import { PhoneNumberPortInPanel } from '@apps/web/components/admin/phone_number_port_in_panel';
import { SchedulerAssessmentsPanel } from '@apps/web/components/admin/scheduler_assessments_panel';
import { StaffImessageNotificationBudgetSection } from '@apps/web/components/admin/staff_imessage_notification_budget_section';
import { PhoneSystemForwardingCard } from '@apps/web/components/settings/phone_system_forwarding_card';
import { SettingsAuditTimeline } from '@apps/web/components/settings/settings_audit_timeline';
import { useSettingsDiscard } from '@apps/web/components/settings/settings_draft_context';
import { Card, CardContent } from '@apps/web/components/ui/card';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@apps/web/components/ui/tabs';
import { getDataSourceDisplayName } from '@apps/web/lib/data_source_utils';
import { DataSyncPage } from '@apps/web/routes/_app/admin/data_sync_page';
import {
    A2pComplianceProfileSection,
    TwilioA2pRegistrationSection,
} from '@apps/web/routes/_app/admin/organizations/a2p_registration_sections';
import { CarePlanSettingsSection } from '@apps/web/routes/_app/admin/organizations/care_plan_settings_section';
import { DataSourceCredentialsSection } from '@apps/web/routes/_app/admin/organizations/data_source_credentials_section';
import { ExternalSmsProviderAccountsSection } from '@apps/web/routes/_app/admin/organizations/external_sms_provider_accounts_section';
import { FeatureFlagsSection } from '@apps/web/routes/_app/admin/organizations/feature_flags_section';
import { GeneralSettingsSection } from '@apps/web/routes/_app/admin/organizations/general_settings_section';
import { InternalDailyDigestSlackChannelSection } from '@apps/web/routes/_app/admin/organizations/internal_daily_digest_slack_channel_section';
import { OpenShiftSettingsSection } from '@apps/web/routes/_app/admin/organizations/open_shift_settings_section';
import { buildOrganizationFormModalInitialData } from '@apps/web/routes/_app/admin/organizations/organization_form_modal_initial_data';
import { OrganizationHeader } from '@apps/web/routes/_app/admin/organizations/organization_header';
import { OrganizationMembersSection } from '@apps/web/routes/_app/admin/organizations/organization_members_section';
import {
    CustomerAccountCard,
    OrganizationProfileCard,
} from '@apps/web/routes/_app/admin/organizations/organization_profile_card';
import { PendingInvitationsSection } from '@apps/web/routes/_app/admin/organizations/pending_invitations_section';
import { PhoneNumbersSection } from '@apps/web/routes/_app/admin/organizations/phone_numbers_section';
import { ShiftConfirmationSettingsSection } from '@apps/web/routes/_app/admin/organizations/shift_confirmation_settings_section';
import { VoipSetupCard } from '@apps/web/routes/_app/admin/organizations/voip_setup_card';
import { useAgentModelOptions } from '@apps/web/swr_data_fetching/admin_agent_prompt_queries';
import {
    useAdminOrganization,
    useAdminOrganizationCarePlanSettings,
    useAdminOrganizationShiftConfirmationSettings,
    useAdminOrganizationSubAccount,
    useOrganizationInvitations,
    useOrganizationMembers,
} from '@apps/web/swr_data_fetching/admin_organization_queries';
import {
    useAdminA2pComplianceProfile,
    useAdminOrganizationDataSourceAccounts,
    useAdminTwilioA2pRegistration,
    useOrganizationExternalSmsProviderAccounts,
    useOrganizationPhoneNumbers,
    useOrganizationVoiceSettings,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const Route = createFileRoute(
    '/_app/admin/organizations/$organization-id',
)({
    head: () => ({
        meta: [
            {
                title: 'Organization Details | Phoebe Admin',
            },
        ],
    }),
    component: OrganizationDetailPage,
});

function OrganizationDetailPage() {
    const { 'organization-id': organizationId } = Route.useParams();
    const [activeTab, setActiveTab] = useState('settings');
    const requestDiscard = useSettingsDiscard();
    const { data: organization } = useAdminOrganization(organizationId);
    const { data: dataSourceAccounts, isLoading: isLoadingDataSourceAccounts } =
        useAdminOrganizationDataSourceAccounts(organizationId);
    const ehrTypes = new Set(
        dataSourceAccounts?.map((account) => account.type),
    );
    const [ehrType] = ehrTypes;
    const ehrTabLabel =
        ehrTypes.size === 1 && ehrType
            ? getDataSourceDisplayName(ehrType)
            : 'EHR settings';

    return (
        <div className="relative h-full w-full">
            <div className="absolute inset-0 overflow-auto p-6">
                <div className="mb-6">
                    <Link
                        to="/admin/organizations"
                        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Organizations
                    </Link>
                </div>
                <Tabs
                    value={activeTab}
                    onValueChange={(tab) => {
                        if (tab !== activeTab) {
                            requestDiscard(() => setActiveTab(tab));
                        }
                    }}
                    className="w-full"
                >
                    <TabsList className="mb-6">
                        <TabsTrigger value="settings">Settings</TabsTrigger>
                        <TabsTrigger
                            value="ehr-settings"
                            aria-label={ehrTabLabel}
                        >
                            {isLoadingDataSourceAccounts ? (
                                <span
                                    className="bg-muted inline-block h-4 w-24 animate-pulse rounded"
                                    aria-hidden={true}
                                />
                            ) : (
                                ehrTabLabel
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="change-history">
                            Change history
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="settings">
                        <OrganizationDetails organizationId={organizationId} />
                    </TabsContent>
                    <TabsContent value="ehr-settings">
                        <div className="space-y-8">
                            <OrganizationSyncSettings
                                key={organizationId}
                                organizationId={organizationId}
                            />
                            <section className="space-y-4">
                                <div>
                                    <h2 className="text-xl font-semibold">
                                        Data sync
                                    </h2>
                                    <p className="text-muted-foreground text-sm">
                                        Run imports and review sync activity for
                                        this organization.
                                    </p>
                                </div>
                                <DataSyncPage
                                    key={organizationId}
                                    organizationId={organizationId}
                                />
                            </section>
                        </div>
                    </TabsContent>
                    <TabsContent value="change-history">
                        <SettingsAuditTimeline
                            organizationId={organizationId}
                            timezone={organization?.timezone ?? 'UTC'}
                        />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function OrganizationDetails({ organizationId }: { organizationId: string }) {
    const { user } = Route.useRouteContext();
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const {
        data: organization,
        isLoading: isLoadingOrganization,
        mutate: mutateOrganization,
    } = useAdminOrganization(organizationId);
    const {
        data: subAccount,
        isLoading: isLoadingSubAccount,
        error: subAccountError,
        mutate: mutateSubAccount,
    } = useAdminOrganizationSubAccount(organizationId);
    const {
        data: a2pComplianceProfile,
        isLoading: isLoadingA2pComplianceProfile,
        mutate: mutateA2pComplianceProfile,
    } = useAdminA2pComplianceProfile(organizationId);
    const {
        data: twilioA2pRegistration,
        isLoading: isLoadingTwilioA2pRegistration,
        mutate: mutateTwilioA2pRegistration,
    } = useAdminTwilioA2pRegistration(organizationId);
    const canRemoveMembers = user?.admin ?? false;
    const phoneSystemMultiLineEnabled =
        organization?.features?.phone_system_multi_line_enabled === true;
    const phoneSystemPortInEnabled =
        organization?.features?.['phone_system_port_in_enabled'] === true;
    const phoneSystemE911Enabled =
        organization?.features?.phone_system_e911_enabled === true;

    const {
        data: membersResponse,
        isLoading: isLoadingMembers,
        mutate: mutateMembers,
    } = useOrganizationMembers(organizationId);
    const members = membersResponse?.members;
    const {
        data: invitationsResponse,
        isLoading: isLoadingInvitations,
        mutate: mutateInvitations,
    } = useOrganizationInvitations(organizationId);
    const invitations = invitationsResponse?.invitations;
    const {
        data: externalSmsProviderAccounts,
        isLoading: isLoadingExternalSmsProviderAccounts,
        mutate: mutateExternalSmsProviderAccounts,
    } = useOrganizationExternalSmsProviderAccounts(organizationId);
    const {
        data: dataSourceAccounts,
        isLoading: isLoadingDataSourceAccounts,
        mutate: mutateDataSourceAccounts,
    } = useAdminOrganizationDataSourceAccounts(organizationId);
    const {
        data: phoneNumbers,
        isLoading: isLoadingPhoneNumbers,
        mutate: mutatePhoneNumbers,
    } = useOrganizationPhoneNumbers(organizationId);
    const {
        data: shiftConfirmationSettings,
        mutate: mutateShiftConfirmationSettings,
    } = useAdminOrganizationShiftConfirmationSettings(organizationId);
    const {
        data: voiceSettings,
        isLoading: isLoadingVoiceSettings,
        mutate: mutateVoiceSettings,
    } = useOrganizationVoiceSettings(organizationId);
    const { data: carePlanSettings, mutate: mutateCarePlanSettings } =
        useAdminOrganizationCarePlanSettings(organizationId);
    const { data: agentModelOptions } = useAgentModelOptions();

    if (isLoadingOrganization) {
        return (
            <div className="flex h-full items-center justify-center">
                <div className="text-muted-foreground flex items-center gap-2">
                    <Spinner />
                    <span>Loading organization details…</span>
                </div>
            </div>
        );
    }

    if (!organization) {
        return (
            <div className="text-muted-foreground flex h-full items-center justify-center">
                Organization not found
            </div>
        );
    }

    return (
        <div className="min-w-0 space-y-6">
            <OrganizationHeader
                organization={organization}
                organizationId={organizationId}
                onEdit={() => setIsEditModalOpen(true)}
            />
            <VoipSetupCard
                organization={organization}
                mutateOrganization={mutateOrganization}
            />
            <OrganizationProfileCard
                organization={organization}
                organizationId={organizationId}
                mutateOrganization={mutateOrganization}
                subAccount={subAccount}
                isLoadingSubAccount={isLoadingSubAccount}
                subAccountError={subAccountError}
                mutateSubAccount={mutateSubAccount}
            />
            <CustomerAccountCard organization={organization} />
            <InternalDailyDigestSlackChannelSection
                organization={organization}
            />
            <div>
                <div className="mb-4">
                    <div className="flex items-center gap-2">
                        <ClipboardList className="text-muted-foreground/70 h-5 w-5" />
                        <h2 className="text-lg font-semibold">Onboarding</h2>
                    </div>
                    <p className="text-muted-foreground mt-1 text-sm">
                        Customer setup progress and the internal checklists CX
                        uses to get the organization live.
                    </p>
                </div>
                <div className="space-y-6">
                    <OnboardingReviewCard organizationId={organizationId} />
                    <OrganizationChecklistPanel
                        organizationId={organizationId}
                        checklistId="pre_onboarding"
                        title="Customer setup form (pre-onboarding)"
                        description="Mirror of the setup checklist the customer works through during pre-onboarding (team, company info, phone intake, EHR access)."
                    />
                    <OrganizationChecklistPanel
                        organizationId={organizationId}
                        checklistId="onboarding"
                        title="Internal deployment checklist"
                        description="Phoebe-internal implementation checklist for getting the organization live (Initial Setup / Train Phoebe / Go Live). Not shown to the customer."
                    />
                    <OrganizationChecklistPanel
                        organizationId={organizationId}
                        checklistId="post_onboarding"
                        title="Internal follow-up checklist (post-onboarding)"
                        description="Phoebe-internal follow-up work after the organization goes live."
                    />
                </div>
            </div>
            <SchedulerAssessmentsPanel organizationId={organizationId} />
            <StaffImessageNotificationBudgetSection
                organization={organization}
            />
            <A2pComplianceProfileSection
                organizationId={organizationId}
                a2pComplianceProfile={a2pComplianceProfile}
                isLoadingA2pComplianceProfile={isLoadingA2pComplianceProfile}
                mutateA2pComplianceProfile={mutateA2pComplianceProfile}
            />
            <TwilioA2pRegistrationSection
                organizationId={organizationId}
                a2pComplianceProfile={a2pComplianceProfile}
                twilioA2pRegistration={twilioA2pRegistration}
                isLoadingTwilioA2pRegistration={isLoadingTwilioA2pRegistration}
                mutateTwilioA2pRegistration={mutateTwilioA2pRegistration}
            />
            <FeatureFlagsSection
                organization={organization}
                organizationId={organizationId}
                mutateOrganization={mutateOrganization}
                members={members}
                isLoadingMembers={isLoadingMembers}
                voiceSettings={voiceSettings}
                isLoadingVoiceSettings={isLoadingVoiceSettings}
                mutateVoiceSettings={mutateVoiceSettings}
            />
            <OpenShiftSettingsSection
                organization={organization}
                mutateOrganization={mutateOrganization}
            />
            <GeneralSettingsSection
                organization={organization}
                mutateOrganization={mutateOrganization}
                agentModelOptions={agentModelOptions}
            />
            <CarePlanSettingsSection
                organizationId={organizationId}
                carePlanSettings={carePlanSettings}
                mutateCarePlanSettings={mutateCarePlanSettings}
            />
            <ShiftConfirmationSettingsSection
                organizationId={organizationId}
                shiftConfirmationSettings={shiftConfirmationSettings}
                mutateShiftConfirmationSettings={
                    mutateShiftConfirmationSettings
                }
                mutateOrganization={mutateOrganization}
            />
            <OrganizationMembersSection
                organizationId={organizationId}
                members={members}
                isLoadingMembers={isLoadingMembers}
                mutateMembers={mutateMembers}
                canRemoveMembers={canRemoveMembers}
            />
            <PendingInvitationsSection
                organizationId={organizationId}
                invitations={invitations}
                isLoadingInvitations={isLoadingInvitations}
                mutateInvitations={mutateInvitations}
            />
            <ExternalSmsProviderAccountsSection
                organizationId={organizationId}
                externalSmsProviderAccounts={externalSmsProviderAccounts}
                isLoadingExternalSmsProviderAccounts={
                    isLoadingExternalSmsProviderAccounts
                }
                mutateExternalSmsProviderAccounts={
                    mutateExternalSmsProviderAccounts
                }
            />
            <PhoneNumbersSection
                organizationId={organizationId}
                phoneNumbers={phoneNumbers}
                isLoadingPhoneNumbers={isLoadingPhoneNumbers}
                mutatePhoneNumbers={mutatePhoneNumbers}
                externalSmsProviderAccounts={externalSmsProviderAccounts}
                phoneSystemMultiLineEnabled={phoneSystemMultiLineEnabled}
                phoneSystemE911Enabled={phoneSystemE911Enabled}
            />
            <div>
                <EhrAccessCard organizationId={organizationId!} />
            </div>
            <div>
                <ExternalPhoneForwardingCard
                    organizationId={organizationId!}
                    phoneNumbers={phoneNumbers}
                />
            </div>
            <div>
                <PhoneSystemForwardingCard
                    organizationId={organizationId!}
                    variant="admin"
                    appearance="classic"
                />
            </div>
            {phoneSystemPortInEnabled ? (
                <div>
                    <PhoneNumberPortInPanel
                        organizationId={organizationId}
                        e911Enabled={phoneSystemE911Enabled}
                    />
                </div>
            ) : null}
            <DataSourceCredentialsSection
                organization={organization}
                organizationId={organizationId}
                mutateOrganization={mutateOrganization}
                dataSourceAccounts={dataSourceAccounts}
                isLoadingDataSourceAccounts={isLoadingDataSourceAccounts}
                mutateDataSourceAccounts={mutateDataSourceAccounts}
            />
            <div>
                <div className="mb-4 flex items-center gap-2">
                    <Settings className="text-muted-foreground/70 h-5 w-5" />
                    <h2 className="text-lg font-semibold">
                        Organization Settings
                    </h2>
                </div>
                <Card className="p-0">
                    <CardContent className="p-6">
                        {organization.shift_confirmation_settings ||
                        organization.callout_settings ||
                        organization.general_settings ? (
                            <pre className="bg-muted overflow-x-auto rounded-md p-4">
                                <code className="text-sm">
                                    {JSON.stringify(
                                        {
                                            shift_confirmation:
                                                organization.shift_confirmation_settings,
                                            callout:
                                                organization.callout_settings,
                                            general:
                                                organization.general_settings,
                                        },
                                        null,
                                        2,
                                    )}
                                </code>
                            </pre>
                        ) : (
                            <div className="text-muted-foreground py-8 text-center">
                                No settings configured
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
            <OrganizationFormModal
                key={organizationId}
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                mode="edit"
                organizationId={organizationId}
                initialData={buildOrganizationFormModalInitialData(
                    organization,
                )}
            />
        </div>
    );
}
